// Global search in the header (decision 31.10.2026): "/" or Ctrl/Cmd+K focuses it, results come grouped while
// typing, arrows and Enter pick one. A hit leads to where it lives: deal, company page, inquiry or the original mail.
// It is the only search field (feedback 01.11.2026): on the board and the company list it also filters what is shown.
import type { SearchResult } from "../api";
import { render, S } from "../state";
import { $, esc, fmtDate } from "../util";
import { renderBoard } from "./board";
import { searchCompanies } from "./firmen";
import { showOriginal } from "./original";

const DEBOUNCE_MS = 200;
const ACTIVITY: Record<string, string> = { note: "Notiz", call: "Anruf", meeting: "Termin", task: "Aufgabe" };
const INQ_STATUS: Record<string, string> = { neu: "offen", rueckfrage: "Rückfrage", deal: "umgewandelt", absage: "abgesagt" };

interface Hit {
  html: string;
  open: () => void;
}

let hits: Hit[] = [];
let active = -1;
let seq = 0;
let timer: ReturnType<typeof setTimeout> | undefined;

/** Escapes the snippet and turns the server's markers into highlights. */
const snippet = (s: string) => esc(s.replace(/\s+/g, " ").trim()).replaceAll("\u0002", "<mark>").replaceAll("\u0003", "</mark>");
const go = (hash: string) => {
  if (location.hash === hash) dispatchEvent(new HashChangeEvent("hashchange"));
  else location.hash = hash;
};

/** An inquiry is shown in the list; finished ones only with "Erledigte zeigen". */
export function openInquiry(id: string, open: boolean) {
  if (!open && !S.inqDone) S.inqDone = true;
  go("#anfragen");
  render();
  // The list may still be loading the finished ones; look again shortly.
  const mark = (tries: number) => {
    const card = document.querySelector<HTMLElement>(`#v-anfragen [data-inq="${CSS.escape(id)}"]`);
    if (card) {
      card.scrollIntoView({ block: "center", behavior: "smooth" });
      card.classList.add("gs-flash");
      setTimeout(() => card.classList.remove("gs-flash"), 1600);
    } else if (tries > 0) setTimeout(() => mark(tries - 1), 300);
  };
  mark(10);
}

function toHits(r: SearchResult): { group: string; items: Hit[] }[] {
  const sub = (s: string) => `<span class="gs-sub">${s}</span>`;
  const deal = (id: string | null, ref: string | null) => (id && ref ? ` · <span class="mono">${esc(ref)}</span>` : "");
  return [
    {
      group: "Deals",
      items: r.deals.map((d) => ({
        html: `<span class="mono">${esc(d.ref)}</span> <b>${esc(d.name)}</b>${sub(`${esc(d.company)} · ${esc(d.stage)}`)}`,
        open: () => go(`#deal-${d.id}`),
      })),
    },
    {
      group: "Firmen",
      items: r.companies.map((c) => ({
        html: `<b>${esc(c.name)}</b>${sub([c.domain, c.city, c.openDeals ? `${c.openDeals} offene Deals` : ""].filter(Boolean).map(esc).join(" · "))}`,
        open: () => go(`#firma-${c.id}`),
      })),
    },
    {
      group: "Kontakte",
      items: r.contacts.map((k) => ({
        html: `<b>${esc(k.name ?? k.email ?? "Kontakt")}</b>${sub([k.email, k.company].filter(Boolean).map(esc).join(" · "))}`,
        open: () => (k.companyId ? go(`#firma-${k.companyId}`) : undefined),
      })),
    },
    {
      group: "Anfragen",
      items: r.inquiries.map((i) => ({
        html: `<b>${esc(i.name ?? i.email)}</b>${sub([i.company, `Stufe ${i.grade}`, INQ_STATUS[i.status] ?? i.status].filter(Boolean).map(esc).join(" · "))}`,
        open: () => (i.dealId ? go(`#deal-${i.dealId}`) : openInquiry(i.id, i.status === "neu" || i.status === "rueckfrage")),
      })),
    },
    {
      group: "Mails",
      items: r.mails.map((m) => ({
        html: `<b>${esc(m.subject || "(ohne Betreff)")}</b>${sub(`${esc(fmtDate(m.receivedAt))} · ${esc(m.from)}${deal(m.dealId, m.dealRef)}`)}<span class="gs-snip">${snippet(m.snippet)}</span>`,
        open: () => {
          if (m.hasOriginal) void showOriginal(m.id);
          else if (m.dealId) go(`#deal-${m.dealId}`);
          else if (m.inquiryId) openInquiry(m.inquiryId, true);
          else if (m.companyId) go(`#firma-${m.companyId}`);
          else if (m.category === "unklar") go("#zuordnen");
        },
      })),
    },
    {
      group: "Anhänge",
      items: r.attachments.map((a) => ({
        html: `<b>${esc(a.filename)}</b>${deal(a.dealId, a.dealRef) ? sub(deal(a.dealId, a.dealRef).slice(3)) : ""}<span class="gs-snip">${snippet(a.snippet)}</span>`,
        open: () => (a.dealId ? go(`#deal-${a.dealId}`) : a.mailId ? void showOriginal(a.mailId) : undefined),
      })),
    },
    {
      group: "Notizen",
      items: r.notes.map((n) => ({
        html: `<b>${esc(ACTIVITY[n.type] ?? n.type)}</b>${sub(`${esc(fmtDate(n.at))}${deal(n.dealId, n.dealRef)}`)}<span class="gs-snip">${snippet(n.snippet)}</span>`,
        open: () => (n.dealId ? go(`#deal-${n.dealId}`) : n.companyId ? go(`#firma-${n.companyId}`) : undefined),
      })),
    },
  ].filter((g) => g.items.length);
}

function show(html: string) {
  const panel = $("#gs-panel");
  panel.innerHTML = html;
  // Wider than the field where there is room, but never past the right edge of the window.
  const field = $("#gsearch").getBoundingClientRect();
  const width = Math.max(field.width, Math.min(520, innerWidth - 32));
  panel.style.width = `${width}px`;
  panel.style.left = `${Math.min(0, innerWidth - 16 - field.left - width)}px`;
  panel.hidden = false;
  $("#gs-input").setAttribute("aria-expanded", "true");
}

function close() {
  $("#gs-panel").hidden = true;
  $("#gs-input").setAttribute("aria-expanded", "false");
  active = -1;
}

function paint(r: SearchResult) {
  const groups = toHits(r);
  hits = groups.flatMap((g) => g.items);
  active = hits.length ? 0 : -1;
  if (!hits.length) return show(`<p class="gs-empty">Nichts gefunden zu „${esc(r.q)}“.</p>`);
  let n = 0;
  show(
    groups
      .map(
        (g) =>
          `<div class="gs-group" role="group" aria-label="${esc(g.group)}"><p class="eyebrow">${esc(g.group)}</p>${g.items
            .map((h) => {
              const i = n++;
              return `<button type="button" class="gs-hit${i === active ? " on" : ""}" role="option" aria-selected="${i === active}" data-hit="${i}">${h.html}</button>`;
            })
            .join("")}</div>`,
      )
      .join(""),
  );
}

function move(by: number) {
  if (!hits.length) return;
  active = (active + by + hits.length) % hits.length;
  document.querySelectorAll<HTMLElement>("#gs-panel [data-hit]").forEach((el) => {
    const on = Number(el.dataset.hit) === active;
    el.classList.toggle("on", on);
    el.setAttribute("aria-selected", String(on));
    if (on) el.scrollIntoView({ block: "nearest" });
  });
}

function pick(i: number) {
  const h = hits[i];
  if (!h) return;
  close();
  $<HTMLInputElement>("#gs-input").blur();
  h.open();
}

async function run(q: string) {
  const mine = ++seq;
  try {
    const r = await S.api.search(q);
    // An answer to an older input arrives late: drop it.
    if (mine === seq && $<HTMLInputElement>("#gs-input").value.trim() === q) paint(r);
  } catch {
    if (mine === seq) show(`<p class="gs-empty">Die Suche hat gerade nicht geklappt. Bitte noch einmal.</p>`);
  }
}

/** The open board or company list takes the query as its filter; returns the view that has to show it anew. */
export function filterOpenView(q: string): "board" | "firmen" | null {
  if (S.view === "board" && S.q !== q) return ((S.q = q), "board");
  if (S.view === "firmen" && S.companyQuery !== q) return ((S.companyQuery = q), "firmen");
  return null;
}

export function bindSearch(): void {
  const box = $("#gsearch");
  const input = $<HTMLInputElement>("#gs-input");
  box.hidden = false;
  input.addEventListener("input", () => {
    const v = filterOpenView(input.value);
    if (v === "board") renderBoard();
    if (v === "firmen") searchCompanies();
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) {
      seq++;
      close();
      return;
    }
    timer = setTimeout(() => void run(q), DEBOUNCE_MS);
  });
  input.addEventListener("focus", () => {
    if (input.value.trim().length >= 2 && hits.length) $("#gs-panel").hidden = false;
  });
  input.addEventListener("keydown", (e) => {
    const step = { ArrowDown: 1, ArrowUp: -1 }[e.key];
    if (step) {
      e.preventDefault();
      move(step);
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(active);
    } else if (e.key === "Escape") {
      if (!$("#gs-panel").hidden) close();
      else input.blur();
    }
  });
  $("#gs-panel").addEventListener("click", (e) => {
    const i = (e.target as HTMLElement).closest<HTMLElement>("[data-hit]")?.dataset.hit;
    if (i !== undefined) pick(Number(i));
  });
  // Mouse down on a hit must not close the panel before the click.
  $("#gs-panel").addEventListener("mousedown", (e) => e.preventDefault());
  document.addEventListener("click", (e) => {
    if (!box.contains(e.target as Node)) close();
    // "Filter aufheben" on the board or the company list empties the field.
    if ((e.target as HTMLElement).closest("[data-clear-search]")) {
      input.value = "";
      input.dispatchEvent(new Event("input"));
    }
  });
  document.addEventListener("keydown", (e) => {
    const typing = (e.target as HTMLElement).closest("input, textarea, select, [contenteditable]");
    if ((e.key === "/" && !typing) || (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey))) {
      e.preventDefault();
      input.focus();
      input.select();
    }
  });
}
